import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PublicContactForm from "@/components/PublicContactForm";
import UrgencyBadge from "@/components/UrgencyBadge";
import { CATEGORIES } from "@/lib/constants";
import {
    ArrowRight,
    Zap,
    Shield,
    FileCheck2,
    CreditCard,
    Users,
    Sparkles,
    CheckCircle2,
    Building2,
    Briefcase,
    MapPin,
    ChevronDown,
} from "lucide-react";

// ---------- Data ----------
const STEPS = [
    {
        icon: Sparkles,
        title: "Décrivez votre besoin",
        text: "Métier, horaires, ville, nombre de personnes, compétences. Renforteo calcule un devis détaillé en direct.",
    },
    {
        icon: Users,
        title: "Recevez des candidatures qualifiées",
        text: "Nos algorithmes proposent la mission en priorité aux profils les plus compatibles à proximité.",
    },
    {
        icon: Shield,
        title: "Gérez tout depuis un seul espace",
        text: "Contrats, feuilles d'heures, paiement sécurisé et facturation — 100 % dématérialisés.",
    },
];

const CATEGORY_IMAGES = {
    hote: "https://images.pexels.com/photos/35533009/pexels-photo-35533009.jpeg?auto=compress&cs=tinysrgb&w=800",
    agent_accueil: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=70",
    agent_securite: "https://images.pexels.com/photos/1464230/pexels-photo-1464230.jpeg?auto=compress&cs=tinysrgb&w=800",
    chauffeur: "https://images.pexels.com/photos/1719648/pexels-photo-1719648.jpeg?auto=compress&cs=tinysrgb&w=800",
    chauffeur_prive: "https://images.pexels.com/photos/1592384/pexels-photo-1592384.jpeg?auto=compress&cs=tinysrgb&w=800",
    groom: "https://images.unsplash.com/photo-1590490360182-c33d57733427?auto=format&fit=crop&w=800&q=70",
    serveur: "https://images.pexels.com/photos/5920774/pexels-photo-5920774.jpeg?auto=compress&cs=tinysrgb&w=800",
    chef_de_rang: "https://images.unsplash.com/photo-1592861956120-e524fc739696?auto=format&fit=crop&w=800&q=70",
    maitre_hotel: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=800&q=70",
    barman: "https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg?auto=compress&cs=tinysrgb&w=800",
    cuisinier: "https://images.pexels.com/photos/3298182/pexels-photo-3298182.jpeg?auto=compress&cs=tinysrgb&w=800",
    plongeur: "https://images.pexels.com/photos/4252140/pexels-photo-4252140.jpeg?auto=compress&cs=tinysrgb&w=800",
    commis: "https://images.pexels.com/photos/2544829/pexels-photo-2544829.jpeg?auto=compress&cs=tinysrgb&w=800",
    livreur: "https://images.pexels.com/photos/4392033/pexels-photo-4392033.jpeg?auto=compress&cs=tinysrgb&w=800",
    femme_de_menage: "https://images.pexels.com/photos/6197118/pexels-photo-6197118.jpeg?auto=compress&cs=tinysrgb&w=800",
    ash: "https://images.pexels.com/photos/7108226/pexels-photo-7108226.jpeg?auto=compress&cs=tinysrgb&w=800",
    manutentionnaire: "https://images.pexels.com/photos/4481323/pexels-photo-4481323.jpeg?auto=compress&cs=tinysrgb&w=800",
    personnel_logistique: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=800&q=70",
    chef_equipe: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=70",
    animateur: "https://images.pexels.com/photos/7648476/pexels-photo-7648476.jpeg?auto=compress&cs=tinysrgb&w=800",
    personnel_evenementiel: "https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg?auto=compress&cs=tinysrgb&w=800",
};

const HOMEPAGE_CATEGORIES = [
    "serveur",
    "hote",
    "maitre_hotel",
    "chef_de_rang",
    "barman",
    "cuisinier",
    "plongeur",
    "commis",
    "femme_de_menage",
    "manutentionnaire",
    "personnel_logistique",
    "chef_equipe",
    "livreur",
    "personnel_evenementiel",
    "animateur",
];

const CONTROLS = [
    "Identité vérifiée par contrôle documentaire",
    "SIRET et statut d'indépendant confirmés",
    "KBIS ou fiche INSEE validés manuellement",
    "Diplômes et qualifications déclarés (formation hygiène alimentaire, etc.)",
    "Évaluations à chaque mission (client & pro)",
    "Suivi des taux de présence et d'annulation",
];

const CITIES = [
    "Lyon", "Villeurbanne", "Caluire-et-Cuire", "Vénissieux",
    "Bron", "Écully", "Meyzieu", "Saint-Priest",
    "Grenoble", "Meylan", "Échirolles", "Voiron",
    "Valence", "Bourg-lès-Valence", "Romans-sur-Isère",
];

const FAQ = [
    {
        q: "Combien coûte Renforteo ?",
        a: "Renforteo est gratuit à l'inscription. Des frais plateforme ne sont prélevés qu'en cas de mise en relation aboutie. Le devis affiché est transparent : honoraires HT du professionnel, frais Renforteo, TVA (selon statut du professionnel) et éventuels frais de déplacement sont détaillés ligne par ligne avant paiement.",
    },
    {
        q: "En combien de temps recevrai-je des candidatures ?",
        a: "Dès la validation de votre devis, votre mission est diffusée en priorité aux professionnels les plus proches et les plus compatibles. Sur Lyon, Grenoble et Valence, les premières candidatures arrivent généralement dans l'heure en journée.",
    },
    {
        q: "Comment sont vérifiés les profils ?",
        a: "Chaque candidat fournit ses pièces d'identité (recto/verso, vérifiées par IA), RIB, justificatif de domicile de moins de 3 mois, et son extrait KBIS ou fiche INSEE. Selon le métier, il peut déclarer ses qualifications hygiène alimentaire (formation OA hygiène / HACCP le cas échéant). Notre équipe valide manuellement chaque profil avant publication. L'obligation légale d'hygiène incombe à l'établissement donneur d'ordre. Les documents sensibles ne sont jamais rendus publics.",
    },
    {
        q: "Quelles sont les conditions de paiement ?",
        a: "Le paiement se fait en ligne via Stripe. Vous pouvez régler un acompte à la commande, puis le solde à la fin de la mission après validation des heures effectivement travaillées. Une facture conforme est générée automatiquement.",
    },
    {
        q: "Que se passe-t-il en cas d'annulation ou d'absence ?",
        a: "En cas d'empêchement, prévenez Renforteo au plus tôt : nous activons notre dispositif de remplacement et proposons la mission aux profils compatibles à proximité. Les conditions financières précises sont détaillées dans nos CGV.",
    },
    {
        q: "Les indications tarifaires sont-elles juridiquement définitives ?",
        a: "Non. L'estimation est indicative : le montant final dépend des heures effectivement travaillées, des frais additionnels validés en amont (déplacements, hébergement, repas) et des éventuelles majorations (nuit, dimanche, jour férié). Tous nos prestataires sont des auto-entrepreneurs / indépendants immatriculés — aucune déclaration sociale n'est à votre charge, ils facturent en direct et gèrent seuls leurs cotisations.",
    },
];

// ---------- Section: Two paths ----------
function TwoPaths() {
    return (
        <section
            className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20"
            data-testid="two-paths-section"
        >
            <div className="text-center mb-12">
                <div className="overline mb-3">Deux parcours, une plateforme</div>
                <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                    Que venez-vous faire sur Renforteo&nbsp;?
                </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
                <Link
                    to="/entreprises"
                    className="group sd-card p-8 md:p-10 border-2 border-transparent hover:border-[#F5A623] transition-all"
                    data-testid="path-business-card"
                >
                    <div className="w-12 h-12 rounded-xl bg-[#0B192C] text-[#F5A623] flex items-center justify-center mb-5">
                        <Building2 size={22} />
                    </div>
                    <div className="overline mb-2">Entreprise</div>
                    <h3 className="font-display text-2xl md:text-3xl mb-3 text-[#0B192C]">
                        Je cherche du personnel
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-6">
                        Publiez votre besoin en quelques minutes, comparez les candidatures qualifiées
                        et pilotez vos missions depuis un tableau de bord dédié.
                    </p>
                    <div className="inline-flex items-center gap-2 text-[#0B192C] font-semibold text-sm group-hover:text-[#F5A623] transition-colors">
                        Découvrir Renforteo Entreprises
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                </Link>

                <Link
                    to="/professionnels"
                    className="group sd-card p-8 md:p-10 border-2 border-transparent hover:border-[#F5A623] transition-all"
                    data-testid="path-pro-card"
                >
                    <div className="w-12 h-12 rounded-xl bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center mb-5">
                        <Briefcase size={22} />
                    </div>
                    <div className="overline mb-2">Professionnel</div>
                    <h3 className="font-display text-2xl md:text-3xl mb-3 text-[#0B192C]">
                        Je cherche des missions
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-6">
                        Créez votre profil, indiquez vos disponibilités et recevez uniquement les missions
                        proches de chez vous, adaptées à votre métier et à votre expérience.
                    </p>
                    <div className="inline-flex items-center gap-2 text-[#0B192C] font-semibold text-sm group-hover:text-[#F5A623] transition-colors">
                        Découvrir Renforteo Pro
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                </Link>
            </div>
        </section>
    );
}

// ---------- Section: FAQ (details/summary, CSS-only) ----------
function Faq() {
    return (
        <section
            className="max-w-4xl mx-auto px-6 md:px-10 py-16"
            data-testid="faq-section"
            id="faq"
        >
            <div className="text-center mb-10">
                <div className="overline mb-3">FAQ</div>
                <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                    Questions fréquentes
                </h2>
            </div>
            <div className="space-y-3">
                {FAQ.map((f, i) => (
                    <details
                        key={i}
                        className="sd-card p-5 md:p-6 group"
                        data-testid={`faq-item-${i}`}
                    >
                        <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                            <span className="font-display text-lg text-[#0B192C]">{f.q}</span>
                            <ChevronDown
                                size={20}
                                className="shrink-0 text-slate-400 mt-1 transition-transform group-open:rotate-180"
                            />
                        </summary>
                        <p className="text-sm text-slate-500 leading-relaxed mt-3">
                            {f.a}
                        </p>
                    </details>
                ))}
            </div>
        </section>
    );
}

// ---------- Main ----------
export default function Landing() {
    return (
        <div>
            <Header />

            {/* ===================== HERO ===================== */}
            <section
                className="relative bg-[#0B192C] text-white overflow-hidden noise"
                data-testid="hero-section"
            >
                <div className="absolute inset-0 bg-gradient-to-br from-[#0B192C] via-[#0B192C] to-[#142238]" />
                <div
                    className="absolute top-0 right-0 w-[55%] h-full opacity-25 hidden md:block"
                    style={{
                        backgroundImage:
                            "url(https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1600&q=80)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        maskImage:
                            "linear-gradient(to left, black 40%, transparent 100%)",
                        WebkitMaskImage:
                            "linear-gradient(to left, black 40%, transparent 100%)",
                    }}
                />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-32">
                    <div className="max-w-3xl fade-up">
                        <div className="pill pill-gold mb-6" data-testid="hero-badge">
                            <Zap size={12} /> Renfort professionnel à Lyon · Devis en 60 s
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white tracking-tight mb-6">
                            Besoin de personnel en renfort à Lyon&nbsp;?{" "}
                            <span className="text-[#F5A623]">Trouvez un professionnel disponible rapidement.</span>
                        </h1>
                        <p className="text-base md:text-lg text-white/75 max-w-2xl mb-4 leading-relaxed">
                            Restauration · Hôtellerie · Événementiel · Nettoyage · Logistique — Renforteo met en relation les entreprises lyonnaises avec des professionnels indépendants vérifiés (SIRET, KBIS, identité, domicile), disponibles souvent en moins de 24&nbsp;h. Publiez votre besoin et sélectionnez directement le profil qui vous convient.
                        </p>
                        <p className="text-sm md:text-base text-white/60 max-w-2xl mb-10 leading-relaxed">
                            Presqu'île, Part-Dieu, Confluence, Villeurbanne, Vieux-Lyon, Eurexpo Chassieu — nos prestataires couvrent l'ensemble de la métropole. Contrat, paiement et facture 100&nbsp;% en ligne. Sans agence d'intérim.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link
                                to="/devis"
                                className="btn-gold"
                                data-testid="hero-cta-estimate"
                            >
                                Estimer ma mission
                                <ArrowRight
                                    className="inline ml-2 -mr-1"
                                    size={18}
                                />
                            </Link>
                            <Link
                                to="/candidat/inscription"
                                className="btn-ghost-navy !text-white !border-white/25 hover:!bg-white/10 hover:!text-white"
                                data-testid="hero-cta-find-mission"
                            >
                                Trouver une mission
                            </Link>
                        </div>

                        <div className="grid grid-cols-3 gap-6 mt-14 max-w-lg">
                            <div>
                                <div className="font-display text-3xl font-black text-[#F5A623]">
                                    &lt;60s
                                </div>
                                <div className="text-xs text-white/60 mt-1">
                                    Devis instantané
                                </div>
                            </div>
                            <div>
                                <div className="font-display text-3xl font-black text-[#F5A623]">
                                    12+
                                </div>
                                <div className="text-xs text-white/60 mt-1">
                                    Métiers couverts
                                </div>
                            </div>
                            <div>
                                <div className="font-display text-3xl font-black text-[#F5A623]">
                                    100%
                                </div>
                                <div className="text-xs text-white/60 mt-1">
                                    Paiement sécurisé
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===================== TWO PATHS ===================== */}
            <TwoPaths />
            <UrgencyBadge />

            {/* ===================== CATEGORIES ===================== */}
            <section
                className="max-w-7xl mx-auto px-6 md:px-10 pb-16"
                data-testid="categories-section"
            >
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <div className="overline mb-3">Métiers couverts</div>
                        <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                            Le bon profil,
                            <br />
                            pour la bonne mission.
                        </h2>
                    </div>
                    <Link to="/devis" className="btn-ghost-navy text-sm">
                        Voir tous les métiers
                    </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {HOMEPAGE_CATEGORIES.map((key) => {
                        const cat = CATEGORIES.find((c) => c.key === key);
                        const img =
                            CATEGORY_IMAGES[key] ||
                            "https://images.pexels.com/photos/35533009/pexels-photo-35533009.jpeg";
                        return (
                            <Link
                                key={key}
                                to={`/devis?cat=${key}`}
                                className="sd-card overflow-hidden group"
                                data-testid={`category-card-${key}`}
                            >
                                <div className="aspect-[4/5] relative overflow-hidden bg-slate-100">
                                    <img
                                        src={img}
                                        alt={cat?.label}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/85 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-3">
                                        <div className="font-display font-bold text-white text-sm md:text-base leading-tight">
                                            {cat?.label}
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* ===================== PROCESS 3 STEPS ===================== */}
            <section
                className="bg-white border-y border-slate-200"
                data-testid="process-section"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
                    <div className="text-center mb-14">
                        <div className="overline mb-3">Fonctionnement</div>
                        <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                            Trois étapes, un renfort fiable.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {STEPS.map((s, i) => (
                            <div key={i} className="sd-card p-8" data-testid={`step-${i + 1}`}>
                                <div className="w-11 h-11 rounded-xl bg-[#F5A623]/15 flex items-center justify-center text-[#F5A623] mb-5">
                                    <s.icon size={22} />
                                </div>
                                <div className="overline mb-2">
                                    Étape {i + 1}
                                </div>
                                <h3 className="font-display text-xl mb-2 text-[#0B192C]">
                                    {s.title}
                                </h3>
                                <p className="text-sm text-slate-500 leading-relaxed">
                                    {s.text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===================== SAVINGS VS INTERIM ===================== */}
            <section
                className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20"
                data-testid="savings-section"
            >
                <div className="sd-card overflow-hidden bg-gradient-to-br from-[#F5A623]/10 via-white to-white border-2 border-[#F5A623]/30">
                    <div className="grid md:grid-cols-12 gap-8 p-8 md:p-12 items-center">
                        <div className="md:col-span-7">
                            <div className="overline text-[#F5A623] mb-3">Comparateur 2026</div>
                            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C] mb-4">
                                Économisez <span className="text-[#F5A623]">30 à 45 %</span> vs l'intérim CHR à Lyon.
                            </h2>
                            <p className="text-slate-600 leading-relaxed mb-6">
                                Un serveur intérim CHR à Lyon coûte en moyenne <b>22,20 € TTC/h</b> (SMIC × coefficient 1,85). Le même serveur indépendant Renforteo coûte <b>~19,50 € TTC/h</b> (17 € HT + 15 % + TVA sur les frais uniquement). Sur 2 personnes × 6 h, l'économie moyenne dépasse <b>32 €</b>.
                            </p>
                            <Link
                                to="/economie-vs-interim-lyon"
                                className="btn-gold text-sm"
                                data-testid="savings-cta"
                            >
                                Simuler mon économie
                                <ArrowRight className="inline ml-2 -mr-1" size={16} />
                            </Link>
                        </div>
                        <div className="md:col-span-5">
                            <div className="bg-[#0B192C] rounded-xl p-6 text-white noise">
                                <div className="text-xs text-white/60 mb-2 uppercase tracking-wider">Exemple type</div>
                                <div className="font-display text-lg mb-4">Mariage viticole Beaujolais · 8 serveurs × 8 h</div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between text-white/70">
                                        <span>Intérim CHR (22,20 € TTC/h)</span>
                                        <b className="text-red-300">1 421 €</b>
                                    </div>
                                    <div className="flex justify-between text-white/70">
                                        <span>Renforteo (17 € HT/h)</span>
                                        <b className="text-white">1 210 €</b>
                                    </div>
                                    <div className="border-t border-white/20 pt-2 flex justify-between font-display text-base">
                                        <span className="text-[#F5A623]">Économie</span>
                                        <b className="text-[#F5A623]">211 € (-14,9 %)</b>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===================== CONTROLS ===================== */}
            <section
                className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20"
                data-testid="controls-section"
            >
                <div className="grid md:grid-cols-12 gap-8 items-start">
                    <div className="md:col-span-5">
                        <div className="overline mb-3">Confiance</div>
                        <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C] mb-4">
                            Les contrôles que nous effectuons.
                        </h2>
                        <p className="text-slate-500 leading-relaxed">
                            Chaque profil est vérifié avant publication. Les documents sensibles restent
                            strictement confidentiels et ne sont jamais rendus publics.
                        </p>
                        <Link
                            to="/candidat/inscription"
                            className="btn-navy inline-block mt-6 text-sm"
                            data-testid="controls-cta"
                        >
                            Voir les critères
                        </Link>
                    </div>
                    <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {CONTROLS.map((c) => (
                            <div
                                key={c}
                                className="flex items-start gap-3 sd-card p-5"
                            >
                                <div className="w-9 h-9 rounded-lg bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center shrink-0">
                                    <FileCheck2 size={18} />
                                </div>
                                <div className="text-sm text-[#0B192C] leading-snug pt-1.5">
                                    {c}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===================== ADVANTAGES ===================== */}
            <section
                className="max-w-7xl mx-auto px-6 md:px-10 pb-16"
                data-testid="value-section"
            >
                <div className="grid md:grid-cols-12 gap-6">
                    <div className="md:col-span-7 bg-[#0B192C] text-white rounded-2xl p-10 md:p-14 relative overflow-hidden noise">
                        <div className="relative">
                            <div className="overline text-white/60 mb-3">
                                Pourquoi Renforteo
                            </div>
                            <h3 className="font-display text-3xl md:text-4xl leading-tight mb-6">
                                Un renfort qualifié,
                                <br />
                                un paiement protégé.
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                {[
                                    "Matching géo prioritaire (rayon défini par le pro)",
                                    "Estimation détaillée avant publication",
                                    "Tarif horaire HT minimum toujours respecté",
                                    "Facture et TVA générées automatiquement",
                                    "Contrats et feuilles d'heures dématérialisés",
                                    "Assistance humaine par email et téléphone",
                                ].map((t) => (
                                    <div
                                        key={t}
                                        className="flex gap-2 text-sm text-white/85"
                                    >
                                        <CheckCircle2
                                            size={16}
                                            className="text-[#F5A623] mt-0.5 shrink-0"
                                        />
                                        <span>{t}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="md:col-span-5 grid gap-5">
                        <div className="sd-card p-6">
                            <div className="overline mb-2 text-[#F5A623]">Exemple de mission</div>
                            <div className="font-display text-lg text-[#0B192C] mb-2">
                                Cocktail de lancement · Lyon 3e
                            </div>
                            <ul className="text-sm text-slate-500 space-y-1 leading-relaxed">
                                <li>• 4 serveurs · 6h · vendredi 18h–00h</li>
                                <li>• Anglais professionnel · tenue fournie</li>
                                <li>• Devis : 1 248,00 € HT · TTC 1 497,60 €</li>
                                <li>• Candidatures reçues en 45 minutes</li>
                            </ul>
                        </div>
                        <div className="sd-card p-6">
                            <div className="overline mb-2 text-[#F5A623]">Exemple de mission</div>
                            <div className="font-display text-lg text-[#0B192C] mb-2">
                                Renfort logistique · Aubervilliers
                            </div>
                            <ul className="text-sm text-slate-500 space-y-1 leading-relaxed">
                                <li>• 6 manutentionnaires · 8h · samedi 6h–14h</li>
                                <li>• Chaussures de sécurité personnelles</li>
                                <li>• Chef d'équipe désigné automatiquement</li>
                                <li>• Feuille d'heures signée en fin de mission</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===================== CITIES ===================== */}
            <section
                className="bg-white border-y border-slate-200"
                data-testid="cities-section"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
                    <div className="text-center mb-10">
                        <div className="overline mb-3 flex items-center justify-center gap-2">
                            <MapPin size={12} /> Zones couvertes
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#0B192C]">
                            Auvergne-Rhône-Alpes en priorité.
                        </h2>
                        <p className="text-slate-500 mt-3 max-w-2xl mx-auto">
                            Renforteo se concentre sur les trois métropoles où nous pouvons garantir
                            un vivier de professionnels vérifiés et disponibles : <b>Lyon</b> (Rhône),
                            <b> Grenoble</b> (Isère) et <b>Valence</b> (Drôme), plus leur périphérie
                            immédiate. D'autres villes seront ouvertes progressivement, une fois notre
                            vivier local pleinement opérationnel.
                        </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
                        {CITIES.map((city) => (
                            <span
                                key={city}
                                className="px-4 py-2 rounded-full border border-slate-200 text-sm text-[#0B192C] bg-white hover:border-[#F5A623] hover:text-[#F5A623] transition-colors"
                                data-testid={`city-${city.toLowerCase()}`}
                            >
                                {city}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===================== FAQ ===================== */}
            <Faq />

            {/* ===================== PUBLIC CONTACT FORM ===================== */}
            <section
                className="max-w-7xl mx-auto px-6 md:px-10 py-12"
                data-testid="public-contact-section"
                id="contact"
            >
                <div className="grid md:grid-cols-12 gap-8 items-start">
                    <div className="md:col-span-5">
                        <div className="overline mb-3 text-[#F5A623]">Contact</div>
                        <h2 className="font-display text-3xl md:text-4xl text-[#0B192C] mb-4 tracking-tight">
                            Une question ?<br />Écrivez-nous.
                        </h2>
                        <p className="text-slate-500 mb-6 leading-relaxed max-w-md">
                            Devis sur-mesure, partenariat, questions RH ou événement particulier — notre équipe vous répond sous 24 h ouvrées.
                        </p>
                        <div className="space-y-3 text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                                ✉️ <a href="mailto:contact@renforteo.fr" className="hover:text-[#F5A623]">contact@renforteo.fr</a>
                            </div>
                            <div className="flex items-center gap-2">
                                📍 Renforteo — 41 rue Jean Moulin, 69300 Caluire-et-Cuire
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                                SIRET 521 636 167 00046
                            </div>
                        </div>
                    </div>
                    <div className="md:col-span-7">
                        <PublicContactForm />
                    </div>
                </div>
            </section>

            {/* ===================== CTA FINAL ===================== */}
            <section
                className="max-w-7xl mx-auto px-6 md:px-10 pb-20"
                data-testid="cta-section"
            >
                <div className="rounded-2xl bg-gradient-to-br from-[#F5A623] to-[#f7b74a] p-10 md:p-16 relative overflow-hidden">
                    <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
                        <div>
                            <h2 className="font-display text-3xl md:text-4xl text-[#0B192C] tracking-tight mb-3">
                                Prêt à démarrer ?
                            </h2>
                            <p className="text-[#0B192C]/80 max-w-lg">
                                Estimez votre mission en 60 secondes, ou créez votre profil professionnel
                                pour recevoir les missions près de chez vous.
                            </p>
                        </div>
                        <div className="md:text-right flex flex-wrap md:justify-end gap-3">
                            <Link
                                to="/devis"
                                className="btn-navy inline-block"
                                data-testid="footer-cta-estimate"
                            >
                                Estimer ma mission
                                <ArrowRight
                                    className="inline ml-2 -mr-1"
                                    size={18}
                                />
                            </Link>
                            <Link
                                to="/candidat/inscription"
                                className="inline-block px-6 py-3 rounded-full font-semibold text-sm border-2 border-[#0B192C] text-[#0B192C] hover:bg-[#0B192C] hover:text-white transition-colors"
                                data-testid="footer-cta-find-mission"
                            >
                                Trouver une mission
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
